// src/components/ContactSupportCenter.js
import React, { useState, useEffect, useContext } from "react";
import {
  TextField,
  Button,
  Typography,
  Container,
  MenuItem,
  CircularProgress,
  Backdrop,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Link,
  Tooltip,
  Snackbar,
  Stack,
  Paper,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { db, auth, storage } from "../api/firebase";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage";

import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import PostContent from "./PostContent";
import ImageRenderer from "./ImageRenderer";
import { AppThemeContext } from "./AppShell";

const categories = [
  "サイトについて",
  "本人確認について",
  "音源について",
  "マスターマインドメンバーズについて",
  "グループ活動について",
  "2025年1月13日、DDP添削会について",
  "その他",
];

export default function ContactSupportCenter() {
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [useDefaultEmail, setUseDefaultEmail] = useState(true);
  const [replyEmail, setReplyEmail] = useState("");

  const [loading, setLoading] = useState(false);

  const [userData, setUserData] = useState({
    name: "",
    email: "",
    isAdmin: false,
  });

  const [faqCategory, setFaqCategory] = useState("");
  const [faqQuestion, setFaqQuestion] = useState("");
  const [faqAnswer, setFaqAnswer] = useState("");
  const [faqImages, setFaqImages] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const navigate = useNavigate();

  // ===== AppShell からテーマを取得 =====
  const themeFromAppShell = useContext(AppThemeContext) || {};
  const accent = themeFromAppShell.railActive || "#5A35FF";
  const textSoft = themeFromAppShell.railText || "rgba(40,40,60,0.8)";
  const surface = themeFromAppShell.surfaceBg || "#FFFFFF";
  const appBg = themeFromAppShell.appBg || "#F5F5F5";
  const border = themeFromAppShell.bottomBorder || "rgba(0,0,0,0.06)";
  const railItemBg = themeFromAppShell.railItemBg || "#FFFFFF";
  const railItemActiveBg = themeFromAppShell.railItemActiveBg || `${accent}14`;

  // アイコンタイル用 / Chip 用の背景
  const iconTileBg = railItemActiveBg;
  const pillBg = railItemActiveBg;

  // ===== データ取得 =====
  useEffect(() => {
    const init = async () => {
      if (auth.currentUser) {
        const userRef = doc(db, "users", auth.currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          setUserData(data);
          setReplyEmail(data.email);
        }
      }

      const faqSnapshot = await getDocs(collection(db, "faqs"));
      const arr = faqSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFaqs(arr);
    };

    init();
  }, []);

  const groupedFaqs = faqs.reduce((g, faq) => {
    const c = faq.category || "その他";
    if (!g[c]) g[c] = [];
    g[c].push(faq);
    return g;
  }, {});

  // ===== お問い合わせ送信（今は画面からは使っていないがロジックは残しておく） =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !message.trim()) return;

    setLoading(true);

    try {
      await addDoc(collection(db, "support"), {
        name: userData.name,
        email: userData.email,
        replyEmail: useDefaultEmail ? userData.email : replyEmail,
        category,
        message,
        groupId: userData.participatingGroup || "",
        groupId2025h: userData.groups2025 || "",
        createdAt: serverTimestamp(),
        isResolved: false,
        userId: auth.currentUser.uid,
        avatar: userData.avatar,
        loginEmail: userData.email,
      });

      setLoading(false);
      navigate("/contact-confirmation");
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // ===== FAQ dialog open =====
  const handleFaqDialogOpen = (faq = null) => {
    if (faq) {
      setFaqCategory(faq.category);
      setFaqQuestion(faq.question);
      setFaqAnswer(faq.answer);
      setFaqImages(
        faq.images ? faq.images.map((img) => ({ url: img.url })) : []
      );
      setEditingFaq(faq);
    } else {
      setFaqCategory("");
      setFaqQuestion("");
      setFaqAnswer("");
      setFaqImages([]);
      setEditingFaq(null);
    }
    setFaqDialogOpen(true);
  };

  const handleFaqDialogClose = () => setFaqDialogOpen(false);

  // ===== FAQ 画像追加 =====
  const handleImageChange = (e) => {
    const files = e.target.files;
    if (!files) return;
    const arr = Array.from(files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setFaqImages((prev) => [...prev, ...arr]);
  };

  // ===== FAQ 画像削除 =====
  const handleImageDelete = async (i) => {
    const target = faqImages[i];
    if (target.url) {
      try {
        const storageRef = ref(
          storage,
          `faqs/${target.url.split("/").pop().split("?")[0]}`
        );
        await deleteObject(storageRef);
      } catch (err) {
        console.error("Error deleting image from storage:", err);
      }
    }
    setFaqImages((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleMoveImage = (index, dir) => {
    setFaqImages((prev) => {
      const arr = [...prev];
      const next = index + dir;
      if (next < 0 || next >= arr.length) return arr;
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  const handleFaqDelete = async (faqId) => {
    setLoading(true);

    const faqDocRef = doc(db, "faqs", faqId);

    try {
      const faqSnapshot = await getDoc(faqDocRef);
      const faqData = faqSnapshot.data();

      if (faqData && faqData.images && Array.isArray(faqData.images)) {
        for (const image of faqData.images) {
          try {
            const storageRef = ref(
              storage,
              `faqs/${image.url.split("/").pop().split("?")[0]}`
            );
            await deleteObject(storageRef);
          } catch (err) {
            console.error("Error deleting image from storage:", err);
          }
        }
      }

      await deleteDoc(faqDocRef);

      const snap = await getDocs(collection(db, "faqs"));
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFaqs(arr);

      setLoading(false);
    } catch (err) {
      console.error("Error deleting FAQ:", err);
      setLoading(false);
    }
  };

  // ===== FAQ 送信/編集 =====
  const uploadFaq = async () => {
    setLoading(true);
    let urls = [];

    for (const img of faqImages) {
      if (img.file) {
        const fileName = `${uuidv4()}-${img.file.name}`;
        const storageRef = ref(storage, `faqs/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, img.file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            () => {},
            (err) => reject(err),
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              urls.push(url);
              resolve();
            }
          );
        });
      } else {
        urls.push(img.url);
      }
    }

    try {
      if (editingFaq) {
        await updateDoc(doc(db, "faqs", editingFaq.id), {
          category: faqCategory,
          question: faqQuestion,
          answer: faqAnswer,
          images: urls.map((u, i) => ({ url: u, order: i })),
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "faqs"), {
          category: faqCategory,
          question: faqQuestion,
          answer: faqAnswer,
          images: urls.map((u, i) => ({ url: u, order: i })),
          createdAt: serverTimestamp(),
        });
      }

      const snap = await getDocs(collection(db, "faqs"));
      setFaqs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

      setFaqDialogOpen(false);
      setFaqImages([]);
      setEditingFaq(null);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const supportEmail = "info@mastermindmembers.net";

  return (
    <Box sx={{ background: appBg, py: { xs: 3, sm: 4 }, minHeight: "100%" }}>
      <Container maxWidth="md">
        {/* ======== ヘッダーカード ======== */}
        <Box
          sx={{
            mb: 3,
            px: { xs: 2, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.94), rgba(255,255,255,0.86))",
            border: `1px solid ${border}`,
            boxShadow: "0 18px 45px rgba(20,16,40,0.12)",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                bgcolor: iconTileBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 10px 24px rgba(20,16,40,0.12)",
              }}
            >
              <HelpOutlineRoundedIcon sx={{ color: accent, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: "0.14em",
                  fontSize: 11,
                  color: textSoft,
                }}
              >
                SUPPORT & CONTACT
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: textSoft }}
              >
                お問い合わせ・サポート
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "rgba(40,40,60,0.7)",
                  maxWidth: 560,
                }}
              >
                お困りごとがあれば、まずは「よくある質問」をご確認ください。
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ======== 上段：メール案内のみ（フォームは今は非表示） ======== */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="stretch"
        >
          <Stack spacing={2.5} sx={{ width: { xs: "100%", md: 360 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${border}`,
                backgroundColor: surface,
                p: 2.5,
                boxShadow: "0 10px 30px rgba(20,16,40,0.08)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: textSoft, mb: 1 }}
              >
                メールでのお問い合わせ
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(40,40,60,0.7)", mb: 1.5 }}
              >
                直接メールでご連絡いただくことも可能です。
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.2,
                  borderRadius: 2.5,
                  bgcolor: railItemBg,
                  border: `1px dashed ${accent}55`,
                  boxShadow: "0 8px 18px rgba(20,16,40,0.10)",
                }}
              >
                <Link
                  href={`mailto:${supportEmail}`}
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    color: accent,
                    mr: 1,
                  }}
                >
                  {supportEmail}
                </Link>
                <Tooltip title="コピー">
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(supportEmail);
                      setSnackbarOpen(true);
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          </Stack>
        </Stack>

        {/* ======== FAQ ======== */}
        <Box id="faq-section" sx={{ mt: 4 }}>
          {/* 見出し＋管理者専用「質問を追加」ボタン */}
          <Box
            sx={{
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: textSoft,
              }}
            >
              よくある質問
            </Typography>

            {userData.isAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={() => handleFaqDialogOpen()} // 新規作成モードでダイアログを開く
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                質問を追加
              </Button>
            )}
          </Box>

          {Object.keys(groupedFaqs).map((cat) => (
            <Box key={cat} sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: textSoft, fontWeight: 700 }}
              >
                {cat}
              </Typography>
              {groupedFaqs[cat].map((faq) => (
                <Accordion
                  key={faq.id}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: surface,
                    "&::before": { display: "none" },
                    border: `1px solid ${border}`,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Chip
                        label={faq.category}
                        size="small"
                        sx={{
                          mr: 1.5,
                          bgcolor: pillBg,
                          color: accent,
                          borderRadius: 99,
                          fontSize: 11,
                          boxShadow: "0 4px 10px rgba(20,16,40,0.08)",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: textSoft,
                        }}
                      >
                        Q. {faq.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(40,40,60,0.85)",
                        whiteSpace: "pre-line",
                        mb: 1,
                      }}
                    >
                      A.&nbsp;
                      <PostContent content={faq.answer} />
                    </Typography>

                    <ImageRenderer images={faq.images} />

                    {userData.isAdmin && (
                      <Box sx={{ mt: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleFaqDialogOpen(faq)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleFaqDelete(faq.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))}
        </Box>
      </Container>

      {/* ===== FAQ 作成・編集 ダイアログ ===== */}
      <Dialog open={faqDialogOpen} onClose={handleFaqDialogClose} fullWidth>
        <DialogTitle>
          {editingFaq ? "よくある質問を編集" : "よくある質問を投稿"}
        </DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            label="カテゴリ"
            margin="normal"
            value={faqCategory}
            select
            onChange={(e) => setFaqCategory(e.target.value)}
          >
            {categories.map((c, i) => (
              <MenuItem key={i} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            required
            fullWidth
            label="質問"
            margin="normal"
            value={faqQuestion}
            onChange={(e) => setFaqQuestion(e.target.value)}
          />

          <TextField
            required
            fullWidth
            label="回答"
            margin="normal"
            multiline
            minRows={4}
            value={faqAnswer}
            onChange={(e) => setFaqAnswer(e.target.value)}
          />

          <Button
            variant="contained"
            component="label"
            startIcon={<PhotoCamera />}
            sx={{ mt: 2 }}
          >
            画像を追加
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={handleImageChange}
            />
          </Button>

          <List>
            {faqImages.map((img, i) => (
              <ListItem key={i} sx={{ display: "flex", alignItems: "center" }}>
                <ListItemIcon>
                  <img
                    src={img.preview || img.url}
                    alt=""
                    style={{
                      width: 96,
                      height: 96,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                </ListItemIcon>

                <IconButton
                  onClick={() => handleMoveImage(i, -1)}
                  disabled={i === 0}
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleMoveImage(i, 1)}
                  disabled={i === faqImages.length - 1}
                >
                  <ArrowDownwardIcon />
                </IconButton>

                <ListItemText primary={`画像 ${i + 1}`} />

                <IconButton onClick={() => handleImageDelete(i)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleFaqDialogClose}>キャンセル</Button>
          <Button onClick={uploadFaq} disabled={loading}>
            {editingFaq ? "更新" : "投稿"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== ローディング ===== */}
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>送信中...</Typography>
      </Backdrop>

      {/* ===== コピー通知 ===== */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={`${supportEmail} をコピーしました`}
      />
    </Box>
  );
}
